<script setup lang="ts">
import { computed } from 'vue'
import { useNotificationStore } from '@/stores/notificationStore'
import { NotificationPosition } from '@/lib/types'
import NotificationCard from '@/components/NotificationCard.vue'

/**
 * Native renderer for the `'native'` driver in `useNotificationStore`.
 * Iterates `store.nativeNotifications` (entries where `driver ===
 * 'native'`) and renders each through the shared
 * `<NotificationCard />` — same chrome + progress bar + pause-on-
 * hover as the PV driver path.
 *
 * Mounted INSIDE the main content region (see `default.vue` /
 * `plain.vue`) — `absolute` positions relative to `<main>` so the
 * stack stays under the top nav bar + alongside the sidebar and
 * never overlaps chrome. `pointer-events-none` on the outer stack
 * lets clicks fall through; each card re-enables pointer events.
 *
 * Position is shared with the PV host via `store.position` — both
 * drivers move together when `usePosition()` is called.
 */
const store = useNotificationStore()

const positionClass = computed<string>(() => {
  switch (store.position) {
    case NotificationPosition.TOP_LEFT:
      return 'top-4 left-4 items-start'
    case NotificationPosition.TOP_CENTER:
      return 'top-4 left-1/2 -translate-x-1/2 items-center'
    case NotificationPosition.TOP_RIGHT:
      return 'top-4 right-4 items-end'
    case NotificationPosition.BOTTOM_LEFT:
      return 'bottom-4 left-4 items-start'
    case NotificationPosition.BOTTOM_CENTER:
      return 'bottom-4 left-1/2 -translate-x-1/2 items-center'
    case NotificationPosition.BOTTOM_RIGHT:
      return 'bottom-4 right-4 items-end'
  }
  return 'top-4 right-4 items-end'
})
</script>

<template>
  <div class="absolute z-30 flex flex-col gap-2 pointer-events-none" :class="positionClass">
    <TransitionGroup name="notif" tag="div" class="flex flex-col gap-2">
      <NotificationCard v-for="n in store.nativeNotifications" :key="n.id" :entry="n" />
    </TransitionGroup>
  </div>
</template>

<style scoped lang="scss">
.notif-enter-active,
.notif-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.notif-enter-from,
.notif-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
